/**
 * Merci : la pagination du carrousel de la section « Sélection ».
 *
 * Le défilement lui-même n'est pas de son ressort : il est natif, par
 * `overflow-x` et `scroll-snap`, donc il fonctionne au doigt et au clavier sans
 * JavaScript. Ce script ne fait que deux choses — marquer le point de la carte
 * en vue, et sauter à une carte au clic. Sans lui, les points restent inertes
 * et le carrousel reste utilisable ; c'est pourquoi ils ne portent aucune
 * information qu'on ne puisse obtenir autrement.
 *
 * L'appariement se fait par **ordre du document** et non par identifiant :
 * `block.id` n'a pas la même valeur selon qu'on le lit depuis la section ou
 * depuis le bloc — voir la note en tête de `merci-edito.js`. Les libellés des
 * points suivent la même contrainte, d'où le « Carte 1 » posé par le gabarit et
 * remplacé ici par le vrai titre.
 *
 * La carte courante se lit sur la **position de défilement**, pas sur ce qui
 * croise le bord gauche. Chaque carte a sa position d'accroche — son bord
 * gauche moins le `scroll-padding` —, ramenée au défilement maximal : la
 * dernière ne peut pas venir se caler à gauche, la piste s'arrête avant. Le
 * point actif est celui de la position la plus proche. Jusqu'au 2026-10-19, un
 * `IntersectionObserver` guettait une bande au bord gauche ; la dernière carte
 * n'y entrait jamais, et c'est l'avant-dernier point qui restait allumé, au
 * défilement comme au clic.
 *
 * Le clic fait défiler la piste seule (`scrollTo`) vers cette même position,
 * sans toucher au défilement vertical de la page comme le faisait
 * `scrollIntoView`.
 */
class MerciSelection extends HTMLElement {
  #abort;

  connectedCallback() {
    const track = this.querySelector('[data-merci-selection-track]');
    const dots = [...this.querySelectorAll('[data-merci-selection-index]')];
    const cards = track ? [...track.querySelectorAll('.merci-selection-card')].slice(0, dots.length) : [];
    if (!track || dots.length === 0 || cards.length === 0) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    /**
     * Les positions d'accroche, relues à chaque fois : elles changent avec la
     * largeur de l'écran, et il y a huit cartes au plus.
     * @returns {number[]}
     */
    const positions = () => {
      const max = track.scrollWidth - track.clientWidth;
      const left = track.getBoundingClientRect().left;
      const padding = parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
      return cards.map((card) => {
        const position = card.getBoundingClientRect().left - left + track.scrollLeft - padding;
        return Math.min(Math.max(position, 0), max);
      });
    };

    const update = () => {
      const current = track.scrollLeft;
      // À égalité, la carte la plus loin l'emporte : si plusieurs cartes de
      // fin partagent le défilement maximal, le bout de la piste allume le
      // dernier point.
      let nearest = 0;
      positions().forEach((position, index, all) => {
        if (Math.abs(position - current) <= Math.abs(all[nearest] - current)) nearest = index;
      });
      this.#setActive(dots, dots[nearest]);
    };

    cards.forEach((card, index) => {
      const dot = dots[index];

      const title = card.querySelector('.merci-selection-card__title')?.textContent?.trim();
      if (title) dot.setAttribute('aria-label', title);

      dot.addEventListener(
        'click',
        () => {
          const position = positions()[index];
          if (position !== undefined) track.scrollTo({ left: position, behavior: 'smooth' });
        },
        { signal }
      );
    });

    track.addEventListener('scroll', update, { passive: true, signal });
    window.addEventListener('resize', update, { passive: true, signal });
    update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  /**
   * @param {HTMLElement[]} dots
   * @param {HTMLElement | undefined} active
   */
  #setActive(dots, active) {
    for (const dot of dots) {
      // `aria-current` porte l'information pour les lecteurs d'écran et sert de
      // crochet au CSS : pas de classe en double.
      if (dot === active) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
  }
}

if (!customElements.get('merci-selection')) {
  customElements.define('merci-selection', MerciSelection);
}
